import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '@klonup/design-system';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function ErrorFallback({ error, onRetry }: { error: Error | null; onRetry: () => void }): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
      <Card style={{ maxWidth: '480px', width: '100%', textAlign: 'center', padding: '2.5rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠</div>
        <h2 className="text-xl font-bold text-klonup-indigo mb-2">
          {t('common.error_boundary.title')}
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '1.5rem' }}>
          {t('common.error_boundary.description')}
        </p>
        {import.meta.env.DEV && error && (
          <pre style={{
            fontSize: '0.75rem',
            color: '#EF4444',
            backgroundColor: '#FEF2F2',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            overflowX: 'auto',
            textAlign: 'left',
            maxHeight: '120px',
          }}>
            {error.message}
          </pre>
        )}
        <Button variant="gradient" onClick={onRetry}>
          {t('common.error_boundary.retry')}
        </Button>
      </Card>
    </div>
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return <ErrorFallback error={this.state.error} onRetry={this.handleReset} />;
  }
}
